import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader, 
  Button, 
  IconButton, 
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Box,
  Paper,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { 
  FaChartLine, 
  FaExchangeAlt, 
  FaNewspaper, 
  FaUsers, 
  FaCoins, 
  FaMoneyBillWave,
  FaCubes,
  FaBook,
  FaPlus,
  FaArrowRight,
  FaInfoCircle,
  FaHeart,
  FaRegCopy,
  FaChartBar
} from 'react-icons/fa';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import { ethers } from 'ethers';
import { useNavigate } from 'react-router-dom';
import { futLogoIcon200, E3D_TOKEN_ABI, E3D_NFT_MANAGER_ABI } from './config';
import axios from 'axios';
import { SERVER_NAME } from './config';

// Keep a small sparkline length for dashboard volume charts
const dashboardSparkLength = 24;

const useStyles = makeStyles((theme) => ({
  dashboardContainer: {
    padding: 0,
    backgroundColor: '#121212',
    minHeight: '100vh',
    color: '#ffffff',
    margin: 0,
    border: 'none',
    boxSizing: 'border-box'
  },
  navBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 24px',
    height: 52,
    backgroundColor: '#1a1a1a',
    borderBottom: '1px solid #333',
    zIndex: 1000,
    fontFamily: theme.typography.fontFamily,
  },
  navLogo: {
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: '#ffffff',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  logoImage: {
    height: 36,
    marginRight: theme.spacing(1),
  },
  logoText: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    color: '#4d79ff',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  navLinks: {
    display: 'flex',
    marginLeft: theme.spacing(4),
  },
  navLink: {
    color: '#ffffff',
    margin: theme.spacing(0, 2),
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    transition: 'color 0.2s',
    '&:hover': {
      color: '#4d79ff',
      textDecoration: 'none',
    },
  },
  active: {
    color: '#4d79ff',
  },
  rightNav: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  walletButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#3a66ff',
    },
  },
  mainContent: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
    textAlign: 'center'
  },
  welcomeText: {
    fontSize: '1.6rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    background: 'linear-gradient(45deg, #4d79ff, #8c64ff)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    color: '#cccccc',
    marginBottom: theme.spacing(2.5),
    fontSize: '0.9rem',
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(0.5),
    },
  },
  groupCheckboxRow: {
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(0.5),
      justifyContent: 'center',
    },
  },
  compactCheckbox: {
    // Desktop: give more space to the left of each checkbox so the
    // group row doesn't feel cramped against the edge.
    marginLeft: theme.spacing(1.5),
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.down('xs')]: {
      // On mobile portrait keep the tighter spacing we tuned earlier.
      marginLeft: theme.spacing(0.25),
    },
    '& .MuiCheckbox-root': {
      paddingRight: theme.spacing(0.25),
      paddingLeft: 0,
    },
    '& .MuiTypography-root': {
      marginLeft: theme.spacing(0.05),
      [theme.breakpoints.down('xs')]: {
        fontSize: '0.75rem',
      },
    },
  },
  timeframeRow: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
    },
  },
  widgetCard: {
    backgroundColor: '#1f2026',
    borderRadius: 15,
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    border: '1px solid #4d79ff',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 10px 20px rgba(77, 121, 255, 0.2)',
    },
  },
  widgetTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid rgba(77, 121, 255, 0.4)',
    backgroundColor: '#272935',
    color: '#ffffff',
    '& h6': {
      color: '#ffffff',
      fontSize: '0.95rem',
    },
  },
  widgetContent: {
    padding: theme.spacing(2),
    color: '#f0f0f0',
    height: 'auto',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#4d79ff',
      borderRadius: '4px',
    },
  },
  textSecondary: {
    color: '#cccccc',
  },
  viewAllButton: {
    textTransform: 'none',
    color: '#4d79ff',
    padding: theme.spacing(0.5, 1),
    fontSize: '0.8rem',
    '&:hover': {
      backgroundColor: 'rgba(77, 121, 255, 0.1)',
    },
  },
  graphPreview: {
    width: '100%',
    height: 200,
    backgroundColor: '#000000',
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    position: 'relative',
  },
  marketplaceItem: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
  },
  nftImage: {
    width: 32,
    height: 32,
    borderRadius: 8,
    marginRight: theme.spacing(2),
    objectFit: 'cover',
    backgroundColor: '#000',
  },
  nftPrice: {
    fontWeight: 'bold',
    color: '#4d79ff',
  },
  tokenItem: {
    padding: theme.spacing(1),
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tokenLogo: {
    width: 32,
    height: 32,
    marginRight: theme.spacing(1),
    borderRadius: '50%',
    backgroundColor: '#333',
    [theme.breakpoints.down('xs')]: {
      width: 24,
      height: 24,
    },
  },
  tokenInfo: {
    flex: 1,
    color: '#ffffff',
  },
  tokenName: {
    fontWeight: 500,
    color: '#ffffff',
    fontSize: '0.9rem',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.75rem',
    },
  },
  tokenPrice: {
    color: '#4d79ff',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.7rem',
    },
  },
  tokenMetrics: {
    textAlign: 'right',
    minWidth: 80,
  },
  sparkline: {
    height: 30,
    width: 80,
  },
  positiveChange: {
    color: '#00e676',
  },
  negativeChange: {
    color: '#ff5252',
  },
  actionButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    borderRadius: 20,
    padding: theme.spacing(0.5, 2),
    fontSize: '0.8rem',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#3a5bbf',
    },
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    marginBottom: theme.spacing(1.5),
    flexWrap: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
      marginBottom: theme.spacing(0.75),
      justifyContent: 'center',
    },
  },
  statCard: {
    padding: theme.spacing(0.75),
    backgroundColor: '#272935',
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '11%',
    minWidth: 90,
    height: 68,
    border: '1px solid #4d79ff',
    [theme.breakpoints.down('xs')]: {
      width: '42%',
      minWidth: 0,
      marginBottom: theme.spacing(0.5),
      height: 54,
      padding: theme.spacing(0.5),
    },
  },
  statValue: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#4d79ff',
    order: 2,
  },
  statLabel: {
    color: '#aaaaaa',
    marginTop: 0,
    marginBottom: theme.spacing(0.25),
    fontSize: '0.65rem',
    order: 1,
  },
  stakingStats: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 8,
  },
  stakingStatItem: {
    textAlign: 'center',
  },
  stakingStatValue: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#4d79ff',
  },
  stakingStatLabel: {
    fontSize: '0.8rem',
    color: '#aaaaaa',
  },
  newsfeedItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 8,
  },
  newsfeedTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
  },
  newsfeedDate: {
    fontSize: '0.8rem',
    color: '#aaaaaa',
  },
  newsfeedExcerpt: {
    fontSize: '0.9rem',
    color: '#cccccc',
    marginTop: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 8,
  },
  activityAvatar: {
    width: 36,
    height: 36,
    marginRight: theme.spacing(1.5),
    backgroundColor: '#333',
  },
  activityContent: {
    flex: 1,
    textAlign: 'left',
  },
  activityTime: {
    fontSize: '0.75rem',
    color: '#aaaaaa',
  },
  storyGroupHeader: {
    fontSize: '0.7rem',
    color: '#888888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  storyMetaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
  },
  storyChip: {
    display: 'inline-block',
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    backgroundColor: 'rgba(77, 121, 255, 0.15)',
    color: '#cccccc',
    fontSize: '0.7rem',
    marginLeft: theme.spacing(0.75),
  },
  storySummary: {
    fontSize: '0.8rem',
    color: '#cccccc',
    marginTop: theme.spacing(0.5),
  },
  callToAction: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  ctaButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    padding: theme.spacing(1.5, 4),
    borderRadius: 30,
    fontSize: '1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#3a5bbf',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 150,
  },
  iconWithLabel: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: theme.spacing(1),
      color: '#4d79ff',
    },
  },
  customCheckboxIcon: {
    width: 14,
    height: 14,
    borderRadius: 3,
    border: '1px solid #4d79ff',
    backgroundColor: 'rgba(77, 121, 255, 0.2)',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    color: '#ffffff',
    lineHeight: 1,
  },
  customCheckboxIconChecked: {
    backgroundColor: '#4d79ff',
  },
}));

const Dashboard = ({ walletAddress, isAuthenticated, userID, tokenList = [] }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [newTokens, setNewTokens] = useState([]);
  const [newestNFTs, setNewestNFTs] = useState([]);
  const [stakedAmount, setStakedAmount] = useState('0');
  const [stakingBenefits, setStakingBenefits] = useState({
    mintingDiscount: '0%',
    royaltyRate: '10%'
  });
  const [topVolumeMap, setTopVolumeMap] = useState(new Map());
  const [recentActivity, setRecentActivity] = useState([]);
  const [recentTheses, setRecentTheses] = useState([]);
  const [storyTokenIcons, setStoryTokenIcons] = useState({}); // address -> icon URL
  const [todayStats, setTodayStats] = useState({
    activeTokens: '0',
    nftsMinted: '0',
    transactions: '0'
  });
  const [dashboardTimeframe, setDashboardTimeframe] = useState('24H');
  const [tokenStats, setTokenStats] = useState([]);
  const [tokenStatsLoading, setTokenStatsLoading] = useState(true);
  
  // Fetch dashboard data when component mounts
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        await Promise.all([
          fetchTokenStats(),
          fetchNewTokens(),
          fetchNewestNFTs(),
          fetchRecentActivity(),
          fetchRecentTheses(),
          fetchTodayStats(),
          fetchTopVolume(),
        ]);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Lightweight timestamp normalizer: accepts either a number (seconds)
  // or a date-like string and returns seconds since epoch.
  const normalizeTimestampToSeconds = (ts) => {
    if (typeof ts === 'number' && Number.isFinite(ts)) return ts;
    if (!ts) return null;
    // Most backend timestamps are "YYYY-MM-DD HH:MM:SS" or ISO-like
    const d = new Date(typeof ts === 'string' ? ts.replace(' ', 'T') : ts);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor(d.getTime() / 1000);
  };

  const formatAddressLabel = (value) => {
    if (!value || typeof value !== 'string') return value;
    const v = value.trim();
    // Basic 0x-prefixed hex address detection, 16-64 hex chars after 0x
    if (/^0x[0-9a-fA-F]{16,64}$/.test(v)) {
      const prefix = v.slice(0, 6);
      const suffix = v.slice(-6);
      return `${prefix}...${suffix}`;
    }
    return value;
  };

  const fetchTopVolume = async () => {
    try {
      // Similar to getTransactions, but simplified for dashboard volume view.
      const dataSourceParam = 'ETH';
      const limit = 5000;
      const startDate = 0;
      const endDate = 0;
      const search = '';

      const response = await axios.get(`${SERVER_NAME}/fetchTransactionsDB/`, {
        params: {
          dataSource: dataSourceParam,
          limit,
          startTime: startDate,
          endTime: endDate,
          search,
          userID,
        },
      });

      let txData = response.data || [];
      if (!Array.isArray(txData) || !txData.length) {
        setTopVolumeMap(new Map());
        return;
      }

      // Step 1: compute min/max timestamps in seconds
      let minTimestamp = Infinity;
      let maxTimestamp = -Infinity;
      txData.forEach((tx) => {
        const ts = normalizeTimestampToSeconds(tx.timestamp);
        if (ts == null) return;
        if (ts < minTimestamp) minTimestamp = ts;
        if (ts > maxTimestamp) maxTimestamp = ts;
      });

      if (!Number.isFinite(minTimestamp) || !Number.isFinite(maxTimestamp) || minTimestamp === maxTimestamp) {
        setTopVolumeMap(new Map());
        return;
      }

      const range = maxTimestamp - minTimestamp;
      const volumeByAddress = new Map();
      const metaByAddress = new Map();

      // Pre-create zeroed arrays for any address that appears
      txData.forEach((tx) => {
        const fromAddr = tx.from;
        const toAddr = tx.to;
        if (fromAddr && !volumeByAddress.has(fromAddr)) {
          volumeByAddress.set(fromAddr, new Array(dashboardSparkLength).fill(0));
        }
        if (toAddr && !volumeByAddress.has(toAddr)) {
          volumeByAddress.set(toAddr, new Array(dashboardSparkLength).fill(0));
        }
      });

      // Increment volume bins
      txData.forEach((tx) => {
        const ts = normalizeTimestampToSeconds(tx.timestamp);
        if (ts == null) return;
        const rel = (ts - minTimestamp) / range;
        let idx = Math.floor(rel * dashboardSparkLength);
        if (idx < 0) idx = 0;
        if (idx >= dashboardSparkLength) idx = dashboardSparkLength - 1;

        const fromAddr = tx.from;
        const toAddr = tx.to;

        if (fromAddr && volumeByAddress.has(fromAddr)) {
          const arr = volumeByAddress.get(fromAddr);
          arr[idx] += 1;
          // Capture basic metadata for this address
          if (!metaByAddress.has(fromAddr)) {
            metaByAddress.set(fromAddr, {
              id: fromAddr,
              name: tx.fromName || tx.name || tx.label || fromAddr,
              symbol: tx.fromSymbol || tx.symbol || '',
              icon: tx.fromIcon || tx.icon || futLogoIcon200,
            });
          }
        }

        if (toAddr && volumeByAddress.has(toAddr)) {
          const arr = volumeByAddress.get(toAddr);
          arr[idx] += 1;
          if (!metaByAddress.has(toAddr)) {
            metaByAddress.set(toAddr, {
              id: toAddr,
              name: tx.toName || tx.name || tx.label || toAddr,
              symbol: tx.toSymbol || tx.symbol || '',
              icon: tx.toIcon || tx.icon || futLogoIcon200,
            });
          }
        }
      });

      // Build the final map with txCount and volume series per address
      const finalMap = new Map();
      volumeByAddress.forEach((series, addr) => {
        const total = series.reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0);
        if (!total) return;
        const meta = metaByAddress.get(addr) || {
          id: addr,
          name: addr,
          symbol: '',
          icon: futLogoIcon200,
        };
        finalMap.set(addr, {
          ...meta,
          txCount: total,
          volume: series,
        });
      });

      setTopVolumeMap(finalMap);
    } catch (error) {
      console.error('Error fetching top volume data for dashboard:', error);
      setTopVolumeMap(new Map());
    }
  };
  
  const fetchTokenStats = async () => {
    try {
      setTokenStatsLoading(true);
      const url = SERVER_NAME + '/fetchTokenPricesWithHistoryAllRanges';
      const response = await axios.get(url, {
        withCredentials: true,
      });
      const rawTokens = response.data || [];

      // We only need a lightweight subset for dashboard stats
      const normalized = rawTokens
        .filter(t => t && t.changes)
        .map(t => ({
          id: (t.address || t.id || '').toLowerCase(),
          symbol: t.symbol || '',
          name: t.name || '',
          priceUSD: t.priceUSD,
          icon: t.icon || t.icon2 || futLogoIcon200,
          changes: t.changes || {},
        }));

      setTokenStats(normalized);
      setTokenStatsLoading(false);
    } catch (error) {
      console.error('Error fetching token stats for dashboard:', error);
      setTokenStats([]);
      setTokenStatsLoading(false);
    }
  };
  
  const fetchTodayStats = async () => {
    try {
      // In a real implementation, you would fetch live stats for today from your API
      // For demonstration, using sample data that looks like it's from today
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;
      console.log(`Fetching stats for today (${formattedDate})`);
      
      // Simulate API call with delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Stats represent:
      // - activeTokens: Number of tokens with activity in the E3D platform today
      // - nftsMinted: New NFTs created from 3D visualizations today
      // - transactions: Number of blockchain transactions processed today
      // - likes: Total number of likes on tokens seen today
      setTodayStats({
        activeTokens: '142',
        nftsMinted: '17',
        transactions: '1.3K',
        likes: '8.6K'
      });
    } catch (error) {
      console.error('Error fetching today\'s stats:', error);
    }
  };
  
  const fetchNewTokens = async () => {
    try {
      // Align "New Tokens" with the /token?new=true view by using the same
      // FilterNewOnly flag against the tokens DB.
      const params = 'FilterNewOnly';
      const dataSourceParam = 'ETH';
      const limit = 5000;
      const startDate = 0; //dateRange.startDate;
      const endDate = 0; //rangeInterval;
      const search = '';

      const response = await axios.get(`${SERVER_NAME}/fetchTransactionsDB/`, {
        params: {
          dataSource: dataSourceParam,
          limit,
          startTime: startDate,
          endTime: endDate,
          search,
          params,       // "FilterNewOnly"
          userID,
        },
      });


      const raw = response.data || [];
      const mapped = raw.map((t, idx) => {
        const toAddressRaw = t.to || t.address;
        const toAddress = typeof toAddressRaw === 'string' ? toAddressRaw.toLowerCase() : toAddressRaw;
        const id = (toAddress || t.id || `new-${idx}`);
        const name = t.toName || t.name || t.label || t.toSymbol || (id && id.substring(0, 8)) || 'New Token';
        const symbol = t.toSymbol || t.symbol || '';
        const logo = t.toIcon || t.icon || t.icon2 || futLogoIcon200;
        // New tokens coming from FilterNewOnly may not have price/24h change yet
        const price = '';
        const change = '';
        const positive = true;
        return { id, name, symbol, logo, price, change, positive };
      })
      // Filter out any rows where we couldn't derive a to-address
      .filter(token => !!token.id);

      // Remove stablecoins from the Latest Tokens list
      const stablecoinSymbols = new Set([
        'USDT', 'USDC', 'DAI', 'TUSD', 'BUSD', 'USDP', 'USDD', 'FRAX', 'GUSD', 'LUSD', 'FDUSD', 'USDX', 'USDK',
      ]);

      const nonStable = mapped.filter((token) => {
        const sym = (token.symbol || '').toUpperCase();
        const nm = (token.name || '').toUpperCase();
        if (stablecoinSymbols.has(sym)) return false;
        // Also drop obvious stablecoins by name pattern
        if (nm.includes('STABLECOIN')) return false;
        if (nm.includes('USD ') || nm.endsWith(' USD')) return false;
        return true;
      });

      // De-duplicate by token id/address so we only show each new token once
      const seenIds = new Set();
      const unique = [];
      for (const token of nonStable) {
        if (seenIds.has(token.id)) continue;
        seenIds.add(token.id);
        unique.push(token);
      }

      // Mirror /token?new=true ordering: sort by label/name descending
      unique.sort((a, b) => {
        if (a.name > b.name) return -1;
        if (a.name < b.name) return 1;
        return 0;
      });

      // Keep the full unique list; we will slice per-view based on listLimit.
      setNewTokens(unique);
    } catch (error) {
      console.error('Error fetching new tokens:', error);
    }
  };
  
  
  const fetchNewestNFTs = async () => {
    try {
      // Direct API call to fetch new Ethereum NFTs created today
      const params = 'FilterNFT,FilterNewOnly';
      const dataSourceParam = 'ETH';
      const limit = 5000;               // or whatever cap you want
      const startDate = 0; //dateRange.startDate;   // or 0 for “latest”
      const endDate = 0; //rangeInterval;           // or 0 for “latest”
      const search = '';

      const response = await axios.get(`${SERVER_NAME}/fetchTransactionsDB/`, {
        params: {
          dataSource: dataSourceParam,
          limit,
          startTime: startDate,
          endTime: endDate,
          search,
          params,        // "FilterNFT,FilterNewOnly"
          userID,        // pass through if you have it
        },
      });
          
      if (response.data && response.data.length > 0) {
        // Format the NFT data for display from transaction to-side fields
        const formattedNFTs = response.data.map((tx, idx) => {
          const toAddressRaw = tx.to || tx.address;
          const toAddress = typeof toAddressRaw === 'string' ? toAddressRaw.toLowerCase() : toAddressRaw;
          // If address is of form 0x...<tokenId>, split on '.' to get contract vs token id
          let contract = toAddress || '';
          let tokenId = '';
          if (contract && contract.includes('.')) {
            const parts = contract.split('.');
            contract = parts[0];
            tokenId = parts[1] || '';
          }

          const baseId = toAddress || contract || `nft-${idx}`;
          const nameBase = tx.toName || tx.name || tx.label || '';
          const name = nameBase
            ? nameBase
            : tokenId
              ? `NFT #${tokenId}`
              : (baseId && baseId.substring(0, 8)) || 'NFT';

          const image = tx.toIcon || tx.icon || tx.image || tx.imageUrl || 'https://via.placeholder.com/100x100?text=NFT';
          const owner = tx.fromName || tx.from || '0x...';
          const createdAt = tx.timestamp
            ? new Date(String(tx.timestamp).replace(' ', 'T')).toISOString()
            : new Date().toISOString();

          return {
            id: baseId,
            name,
            image,
            owner,
            contract: contract || baseId,
            tokenId,
            createdAt,
          };
        });
        
        setNewestNFTs(formattedNFTs);
        console.log('Successfully fetched new NFTs:', formattedNFTs);
      } else {
        // Try alternative endpoint if the first one returns no data
        const altResponse = await axios.get(`${SERVER_NAME}/fetchNFTsDB?new=true`);
        
        if (altResponse.data && altResponse.data.length > 0) {
          const formattedNFTs = altResponse.data.slice(0, 4).map(nft => ({
            id: nft.id || nft.address || `nft-${Math.random().toString(36).substr(2, 9)}`,
            name: nft.name || nft.label || `NFT #${nft.id?.substring(0, 8) || Math.floor(Math.random() * 10000)}`,
            image: nft.image || nft.icon || nft.imageUrl || 'https://via.placeholder.com/100x100?text=NFT',
            price: nft.price ? `${nft.price} ETH` : 'N/A',
            owner: nft.owner || nft.creator || '0x...',
            contract: nft.contractAddress || nft.id,
            createdAt: nft.createdAt || nft.timestamp || new Date().toISOString()
          }));
          
          setNewestNFTs(formattedNFTs);
        } else {
          console.log('No NFTs returned from API');
        }
      }
    } catch (error) {
      console.error('Error fetching newest NFTs:', error);
    }
  };
  
 
  
  const fetchStakingInfo = async (address) => {
    try {
      // In a real implementation, you would call your E3D contracts here
      // For demonstration, using sample data
      setStakedAmount('10.00');
      setStakingBenefits({
        mintingDiscount: '10%',
        royaltyRate: '10%'
      });
    } catch (error) {
      console.error('Error fetching staking info:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      // Reuse the Online Stories feed as "Recent Activity" on the dashboard.
      const res = await fetch(`${SERVER_NAME}/stories`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!Array.isArray(data)) {
        setRecentActivity([]);
        return;
      }
      // Keep only the latest N stories for the widget
      const sorted = [...data].sort((a, b) => (b.ts_created || '').localeCompare(a.ts_created || ''));
      // Keep the full sorted list; limit is applied at render time.
      setRecentActivity(sorted);
    } catch (error) {
      console.error('Error fetching recent activity stories:', error);
      setRecentActivity([]);
    }
  };

  const fetchRecentTheses = async () => {
    try {
      const resp = await axios.get(`${SERVER_NAME}/theses`, {
        params: { status: 'active,confirmed', limit: 50 },
        withCredentials: true,
      });
      const theses = resp && resp.data && Array.isArray(resp.data.theses) ? resp.data.theses : [];
      const sorted = [...theses].sort((a, b) => {
        const ta = (a && (a.ts_updated || a.ts_created)) ? String(a.ts_updated || a.ts_created) : '';
        const tb = (b && (b.ts_updated || b.ts_created)) ? String(b.ts_updated || b.ts_created) : '';
        return tb.localeCompare(ta);
      });
      setRecentTheses(sorted);
    } catch (error) {
      console.error('Error fetching recent theses:', error);
      setRecentTheses([]);
    }
  };

  const navigateTo = (path) => {
    navigate(path);
  };

const formatAddress = (address) => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-6)}`;
};

const formatStoryTime = (ts) => {
  if (!ts) return '';
  const date = new Date(ts.replace(' ', 'T'));
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.max(Math.floor(diffMs / 1000), 0);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d ago`;
};

const extractStoryAddress = (story) => {
  if (!story) return '';
  const raw =
    story.address ||
    story.token_address ||
    story.contract_address ||
    story.contract ||
    story.token ||
    '';
  return typeof raw === 'string' ? raw.toLowerCase() : '';
};

const getStoryTokenIcon = (story) => {
  const addr = extractStoryAddress(story);
  if (!addr) return null;

  // Use any icon we've already fetched and cached for this address
  if (storyTokenIcons && storyTokenIcons[addr]) {
    return storyTokenIcons[addr];
  }

  return null;
};

const ensureStoryTokenIcon = async (story) => {
  const addr = extractStoryAddress(story);
  if (!addr || storyTokenIcons[addr]) return;
  try {
    const res = await axios.get(`${SERVER_NAME}/fetchTokensDB`, {
      params: {
        dataSource: 'ETHERSCAN',
        search: addr,
      },
    });
    const arr = Array.isArray(res.data) ? res.data : [];
    if (!arr.length) return;
    const token = arr[0];
    if (!token || !token.icon) return;
    setStoryTokenIcons((prev) => ({ ...prev, [addr]: token.icon }));
  } catch (e) {
    console.error('Error fetching token icon for story address', addr, e);
  }
};

const groupStoriesByTime = (stories, limit) => {
  if (!stories || stories.length === 0) return {};
  const buckets = {
    'Last 15 minutes': [],
    'Last hour': [],
    'Today': [],
    'Earlier': [],
  };
  const now = new Date();
  const slice = typeof limit === 'number' ? stories.slice(0, limit) : stories;
  slice.forEach((story) => {
    if (!story || !story.ts_created) {
      buckets['Earlier'].push(story);
      return;
    }
    const date = new Date(String(story.ts_created).replace(' ', 'T'));
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.max(diffMs / 60000, 0);
    let key;
    if (diffMin <= 15) key = 'Last 15 minutes';
    else if (diffMin <= 60) key = 'Last hour';
    else if (diffMin <= 1440) key = 'Today';
    else key = 'Earlier';
    buckets[key].push(story);
  });
  Object.keys(buckets).forEach((k) => {
    if (!buckets[k].length) delete buckets[k];
  });
  return buckets;
};

const groupThesesByTime = (theses, limit) => {
  if (!theses || theses.length === 0) return {};
  const buckets = {
    'Last 15 minutes': [],
    'Last hour': [],
    'Today': [],
    'Earlier': [],
  };
  const now = new Date();
  const slice = typeof limit === 'number' ? theses.slice(0, limit) : theses;
  slice.forEach((thesis) => {
    if (!thesis) {
      buckets['Earlier'].push(thesis);
      return;
    }
    const ts = thesis.ts_updated || thesis.ts_created;
    if (!ts) {
      buckets['Earlier'].push(thesis);
      return;
    }
    const date = new Date(String(ts).replace(' ', 'T'));
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.max(diffMs / 60000, 0);
    let key;
    if (diffMin <= 15) key = 'Last 15 minutes';
    else if (diffMin <= 60) key = 'Last hour';
    else if (diffMin <= 1440) key = 'Today';
    else key = 'Earlier';
    buckets[key].push(thesis);
  });
  Object.keys(buckets).forEach((k) => {
    if (!buckets[k].length) delete buckets[k];
  });
  return buckets;
};

  const getChangeForTimeframe = (token, timeframe) => {
    if (!token || !token.changes) return null;
    const entry = token.changes[timeframe];
    if (!entry || typeof entry.percent !== 'number') return null;
    return entry.percent;
  };
  
  const computeMarketOverview = () => {
    if (!tokenStats || tokenStats.length === 0) {
      return {
        tokensUp: 0,
        tokensDown: 0,
        medianChange: 0,
        avgChange: 0,
      };
    }
    const changes = tokenStats
      .map(t => getChangeForTimeframe(t, dashboardTimeframe))
      .filter(v => typeof v === 'number');
    if (changes.length === 0) {
      return {
        tokensUp: 0,
        tokensDown: 0,
        medianChange: 0,
        avgChange: 0,
      };
    }
    const tokensUp = changes.filter(v => v > 0).length;
    const tokensDown = changes.filter(v => v < 0).length;
    const sorted = [...changes].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const medianChange = sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];
    const avgChange = changes.reduce((sum, v) => sum + v, 0) / changes.length;
    return { tokensUp, tokensDown, medianChange, avgChange };
  };
  
  const marketOverview = computeMarketOverview();
  const advDecRatio = marketOverview.tokensDown === 0
    ? marketOverview.tokensUp
    : (marketOverview.tokensUp / marketOverview.tokensDown);
  
  const computeTopMovers = (limit = 10) => {
    if (!tokenStats || tokenStats.length === 0) {
      return { gainers: [], losers: [] };
    }
    const entries = tokenStats
      .map(t => ({ token: t, change: getChangeForTimeframe(t, dashboardTimeframe) }))
      .filter(x => typeof x.change === 'number');
    if (entries.length === 0) {
      return { gainers: [], losers: [] };
    }
    const sorted = [...entries].sort((a, b) => b.change - a.change);
    const gainers = sorted.slice(0, limit);
    const losers = [...sorted].reverse().slice(0, limit);
    return { gainers, losers };
  };

  const computeTopByPrice = (limit = 10) => {
    if (!tokenStats || tokenStats.length === 0) {
      return [];
    }
    const entries = tokenStats
      .filter(t => typeof t.priceUSD === 'number')
      .map(t => ({ token: t, price: t.priceUSD }));
    if (entries.length === 0) return [];
    const sorted = [...entries].sort((a, b) => b.price - a.price);
    return sorted.slice(0, limit);
  };

  const computeTopByVolume = (limit = 10) => {
    if (!topVolumeMap || topVolumeMap.size === 0) return [];
    const all = Array.from(topVolumeMap.values());
    const sorted = all
      .filter((entry) => typeof entry.txCount === 'number' && entry.txCount > 0)
      .sort((a, b) => b.txCount - a.txCount);
    return sorted.slice(0, limit);
  };

  const timeframeOptions = ['30M', '6H', '12H', '24H'];

  const [visibleGroups, setVisibleGroups] = useState([
    'overview',
    'movers',
    'tokens',
    'volume',
    'nfts',
    'stories',
    'theses',
  ]);

  // When multiple widget groups are visible, keep lists compact so cards align.
  // When a single group is visible, allow much deeper lists.
  const nonOverviewVisible = visibleGroups.filter((g) => g !== 'overview');
  const isSingleGroupView = nonOverviewVisible.length === 1;
  const listLimit = nonOverviewVisible.length > 1 || nonOverviewVisible.length === 0 ? 8 : 100;

  const topMovers = computeTopMovers(listLimit);
  const topByPrice = computeTopByPrice(listLimit);
  const topByVolume = computeTopByVolume(listLimit);
  const maxTopTokenPrice = topByPrice.length
    ? Math.max(...topByPrice.map(({ price }) => (typeof price === 'number' ? price : 0)))
    : 0;

  const isMobilePortrait =
    typeof window !== 'undefined'
      ? window.innerWidth <= 600 && window.innerHeight >= window.innerWidth
      : false;

  const allGroups = ['overview', 'movers', 'tokens', 'volume', 'nfts', 'stories', 'theses'];
  // We only render checkboxes for non-overview groups; Overview is always present
  // so the timeframe toggles make sense.
  const groupDefs = [
    { id: 'movers', label: 'Movers' },
    { id: 'tokens', label: 'Tokens' },
    { id: 'volume', label: 'Volume' },
    { id: 'nfts', label: 'NFTs' },
    { id: 'stories', label: 'Stories' },
    { id: 'theses', label: 'Theses' },
  ];

  const toggleGroup = (id) => {
    setVisibleGroups((prev) => {
      const exists = prev.includes(id);
      if (exists) {
        const next = prev.filter((g) => g !== id);
        // Avoid ending up with zero groups; fall back to overview.
        return next.length ? next : ['overview'];
      }
      return [...prev, id];
    });
  };
  
  return (
    <div className={classes.dashboardContainer}>
      <div className={classes.mainContent}>
        {/* Market Overview Widget driven by token history */}
        {visibleGroups.includes('overview') && (
          <Card className={classes.widgetCard} style={{ marginBottom: 16 }}>
            <div
              className={classes.widgetContent}
              style={isSingleGroupView && !nonOverviewVisible.length ? { maxHeight: 'calc(100vh - 220px)' } : undefined}
            >
              {tokenStatsLoading ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress style={{ color: '#4d79ff' }} />
                </div>
              ) : (
                <>
                  <div className={classes.statsContainer}>
                    {isMobilePortrait ? (
                      <>
                        <div className={classes.statCard}>
                          <Typography className={classes.statValue}>{advDecRatio.toFixed(2)}</Typography>
                          <Typography className={classes.statLabel}>Adv / Dec Ratio</Typography>
                        </div>
                        <div className={classes.statCard}>
                          <Typography className={classes.statValue}>
                            {marketOverview.medianChange.toFixed(2)}%
                          </Typography>
                          <Typography className={classes.statLabel}>Median Change</Typography>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={classes.statCard}>
                          <Typography className={classes.statValue}>{marketOverview.tokensUp}</Typography>
                          <Typography className={classes.statLabel}>Tokens Up</Typography>
                        </div>
                        <div className={classes.statCard}>
                          <Typography className={classes.statValue}>{marketOverview.tokensDown}</Typography>
                          <Typography className={classes.statLabel}>Tokens Down</Typography>
                        </div>
                        <div className={classes.statCard}>
                          <Typography className={classes.statValue}>{advDecRatio.toFixed(2)}</Typography>
                          <Typography className={classes.statLabel}>Adv / Dec Ratio</Typography>
                        </div>
                        <div className={classes.statCard}>
                          <Typography className={classes.statValue}>
                            {marketOverview.medianChange.toFixed(2)}%
                          </Typography>
                          <Typography className={classes.statLabel}>Median Change</Typography>
                        </div>
                        <div className={classes.statCard}>
                          <Typography className={classes.statValue}>{todayStats.activeTokens}</Typography>
                          <Typography className={classes.statLabel}>Active Tokens Today</Typography>
                        </div>
                        <div className={classes.statCard}>
                          <Typography className={classes.statValue}>{todayStats.nftsMinted}</Typography>
                          <Typography className={classes.statLabel}>NFTs Minted Today</Typography>
                        </div>
                        <div className={classes.statCard}>
                          <Typography className={classes.statValue}>{todayStats.transactions}</Typography>
                          <Typography className={classes.statLabel}>Transactions Today</Typography>
                        </div>
                      </>
                    )}
                  </div>
                  <Box mt={1} className={classes.controlsRow}>
                    <Box className={classes.groupCheckboxRow}>
                      {groupDefs.map((g) => {
                        const checked = visibleGroups.includes(g.id);
                        return (
                          <FormControlLabel
                            key={`chk-${g.id}`}
                            className={classes.compactCheckbox}
                            control={(
                              <Checkbox
                                color="primary"
                                size="small"
                                checked={checked}
                                onChange={() => toggleGroup(g.id)}
                                icon={<span className={classes.customCheckboxIcon} />}
                                checkedIcon={(
                                  <span
                                    className={`${classes.customCheckboxIcon} ${classes.customCheckboxIconChecked}`}
                                  >
                                    ✓
                                  </span>
                                )}
                              />
                            )}
                            label={(
                              <Typography
                                variant="body2"
                                style={{
                                  color: checked ? '#4d79ff' : '#666666',
                                  fontWeight: checked ? 600 : 400,
                                }}
                              >
                                {g.label}
                              </Typography>
                            )}
                            style={{ marginRight: 8 }}
                          />
                        );
                      })}
                    </Box>
                    <Box className={classes.timeframeRow}>
                      {timeframeOptions.map((tf) => (
                        <Button
                          key={tf}
                          size="small"
                          variant={dashboardTimeframe === tf ? 'contained' : 'text'}
                          color="primary"
                          onClick={() => setDashboardTimeframe(tf)}
                          style={{ marginLeft: 4 }}
                        >
                          {tf}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </div>
          </Card>
        )}
        
        {/* Main Content Grid */}
        <Grid container spacing={3}>

        {/* Top Gainers & Top Losers (Movers group) */}
        {visibleGroups.includes('movers') && (
          <>
            {/* Top Gainers Widget */}
            <Grid item xs={12} md={6}>
              <Card className={classes.widgetCard}>
                <div className={classes.widgetTitle}>
                  <div className={classes.iconWithLabel}>
                    <FaChartLine size={16} />
                    <Typography variant="h6">Top Gainers</Typography>
                  </div>
                </div>
                <div
                  className={classes.widgetContent}
                  style={isSingleGroupView && nonOverviewVisible.includes('movers') ? { maxHeight: 'calc(100vh - 260px)' } : undefined}
                >
                  {tokenStatsLoading ? (
                    <div className={classes.loadingContainer}>
                      <CircularProgress style={{ color: '#4d79ff' }} />
                    </div>
                  ) : (
                    topMovers.gainers.map(({ token, change }) => (
                      <div key={token.id} className={classes.tokenItem}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <img
                            src={token.icon}
                            alt={token.symbol || token.name}
                            className={classes.tokenLogo}
                          />
                          <div className={classes.tokenInfo}>
                            <Typography
                              className={classes.tokenName}
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (token.id) navigateTo(`/links/${token.id}`);
                              }}
                            >
                              {token.name || token.symbol} ({token.symbol})
                            </Typography>
                          </div>
                        </div>
                        <div className={classes.tokenMetrics}>
                          <Typography className={classes.tokenPrice}>
                            {typeof token.priceUSD === 'number'
                              ? `$${token.priceUSD.toFixed(4)}`
                              : 'N/A'}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={change >= 0 ? classes.positiveChange : classes.negativeChange}
                          >
                            {change.toFixed(2)}%
                          </Typography>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </Grid>

            {/* Top Losers Widget */}
            <Grid item xs={12} md={6}>
              <Card className={classes.widgetCard}>
                <div className={classes.widgetTitle}>
                  <div className={classes.iconWithLabel}>
                    <FaChartLine size={16} />
                    <Typography variant="h6">Top Losers</Typography>
                  </div>
                </div>
                <div
                  className={classes.widgetContent}
                  style={isSingleGroupView && nonOverviewVisible.includes('movers') ? { maxHeight: 'calc(100vh - 260px)' } : undefined}
                >
                  {tokenStatsLoading ? (
                    <div className={classes.loadingContainer}>
                      <CircularProgress style={{ color: '#4d79ff' }} />
                    </div>
                  ) : (
                    topMovers.losers.map(({ token, change }) => (
                      <div key={token.id} className={classes.tokenItem}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <img
                            src={token.icon}
                            alt={token.symbol || token.name}
                            className={classes.tokenLogo}
                          />
                          <div className={classes.tokenInfo}>
                            <Typography
                              className={classes.tokenName}
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                if (token.id) navigateTo(`/links/${token.id}`);
                              }}
                            >
                              {token.name || token.symbol} ({token.symbol})
                            </Typography>
                          </div>
                        </div>
                        <div className={classes.tokenMetrics}>
                          <Typography className={classes.tokenPrice}>
                            {typeof token.priceUSD === 'number'
                              ? `$${token.priceUSD.toFixed(4)}`
                              : 'N/A'}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={change >= 0 ? classes.positiveChange : classes.negativeChange}
                          >
                            {change.toFixed(2)}%
                          </Typography>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </Grid>
          </>
        )}

        {/* Top Volume Widget (transaction volume for selected timeframe) */}
        {visibleGroups.includes('volume') && (
          <Grid item xs={12} md={6}>
            <Card className={classes.widgetCard}>
              <div className={classes.widgetTitle}>
                <div className={classes.iconWithLabel}>
                  <FaChartBar size={16} />
                  <Typography variant="h6">Top Volume</Typography>
                </div>
              </div>
              <div className={classes.widgetContent}>
                {loading ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress style={{ color: '#4d79ff' }} />
                  </div>
                ) : (
                  topByVolume.map(({ id, name, symbol, icon, txCount, volume }) => (
                    <div key={id} className={classes.tokenItem}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <img
                          src={icon || futLogoIcon200}
                          alt={symbol || name || id}
                          className={classes.tokenLogo}
                        />
                        <div className={classes.tokenInfo}>
                          <Typography
                            className={classes.tokenName}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (id) navigateTo(`/links/${id}`);
                            }}
                          >
                            {formatAddressLabel(name || symbol || id)}{symbol ? ` (${symbol})` : ''}
                          </Typography>
                        </div>
                      </div>
                      <div className={classes.tokenMetrics}>
                        <Typography className={classes.tokenPrice}>
                          {txCount.toLocaleString()} tx
                        </Typography>
                        {Array.isArray(volume) && volume.length > 1 && (
                          <div className={classes.sparkline}>
                            <Sparklines data={volume} svgHeight={30} svgWidth={80}>
                              <SparklinesLine style={{ stroke: '#4d79ff', fill: 'none', strokeWidth: 2 }} />
                            </Sparklines>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </Grid>
        )}
        
        {/* Top Tokens + Latest Tokens (Tokens group) */}
        {visibleGroups.includes('tokens') && (
          <>
            {/* Top Tokens Widget (highest-priced tokens from price meta) */}
            <Grid item xs={12} md={6}>
              <Card className={classes.widgetCard}>
                <div className={classes.widgetTitle}>
                  <div className={classes.iconWithLabel}>
                    <FaCoins size={16} />
                    <Typography variant="h6">Top Tokens</Typography>
                  </div>
                  <Button 
                    className={classes.viewAllButton}
                    onClick={() => navigateTo('/token')}
                    endIcon={<FaArrowRight />}
                  >
                    View All Tokens
                  </Button>
                </div>
                <div className={classes.widgetContent}>
                  {tokenStatsLoading ? (
                    <div className={classes.loadingContainer}>
                      <CircularProgress style={{ color: '#4d79ff' }} />
                    </div>
                  ) : (
                    <>
                      {topByPrice.map(({ token, price }) => (
                        <div key={token.id} className={classes.tokenItem}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img 
                              src={token.icon}
                              alt={token.name} 
                              className={classes.tokenLogo}
                            />
                            <div className={classes.tokenInfo}>
                              <Typography
                                className={classes.tokenName}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  if (token.id) navigateTo(`/links/${token.id}`);
                                }}
                              >
                                {token.name} ({token.symbol})
                              </Typography>
                            </div>
                          </div>
                          <div className={classes.tokenMetrics}>
                            <Typography className={classes.tokenPrice}>
                              {typeof price === 'number' ? `$${price.toFixed(4)}` : 'N/A'}
                            </Typography>
                            {typeof price === 'number' && maxTopTokenPrice > 0 && (
                              <div
                                style={{
                                  marginTop: 4,
                                  width: 80,
                                  height: 4,
                                  borderRadius: 4,
                                  backgroundColor: '#1b1c22',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    width: `${Math.max(5, (price / maxTopTokenPrice) * 100)}%`,
                                    height: '100%',
                                    borderRadius: 4,
                                    background: 'linear-gradient(90deg, #4d79ff, #8c64ff)',
                                  }}
                                />
                              </div>
                            )}
                            {(() => {
                              const change = getChangeForTimeframe(token, dashboardTimeframe);
                              if (typeof change !== 'number') return null;
                              const positive = change >= 0;
                              return (
                                <Typography
                                  variant="body2"
                                  className={positive ? classes.positiveChange : classes.negativeChange}
                                >
                                  {change.toFixed(2)}%
                                </Typography>
                              );
                            })()}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </Card>
            </Grid>
            
            {/* Latest Tokens Widget (true newest tokens from FilterNewOnly) */}
            <Grid item xs={12} md={6}>
              <Card className={classes.widgetCard}>
                <div className={classes.widgetTitle}>
                  <div className={classes.iconWithLabel}>
                    <FaCoins size={16} />
                    <Typography variant="h6">Latest Tokens</Typography>
                  </div>
                  <Button 
                    className={classes.viewAllButton}
                    onClick={() => navigateTo('/token?new=true')}
                    endIcon={<FaArrowRight />}
                  >
                    View New Tokens
                  </Button>
                </div>
                <div className={classes.widgetContent}>
                  {loading ? (
                    <div className={classes.loadingContainer}>
                      <CircularProgress style={{ color: '#4d79ff' }} />
                    </div>
                  ) : (
                    <>
                      {newTokens.slice(0, listLimit).map((token) => (
                        <div
                          key={token.id}
                          className={classes.tokenItem}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (token.id) navigateTo(`/links/${token.id}`);
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img 
                              src={token.logo} 
                              alt={token.name} 
                              className={classes.tokenLogo}
                            />
                            <div className={classes.tokenInfo}>
                              <Typography className={classes.tokenName}>
                                {token.name} ({token.symbol})
                              </Typography>
                            </div>
                          </div>
                          <div>
                            <Typography className={classes.tokenPrice}>
                              {token.price}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              className={token.positive ? classes.positiveChange : classes.negativeChange}
                            >
                              {token.change}
                            </Typography>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </Card>
            </Grid>
          </>
        )}
        
        {/* Latest NFTs Widget */}
        {visibleGroups.includes('nfts') && (
          <Grid item xs={12} md={6}>
            <Card className={classes.widgetCard}>
              <div className={classes.widgetTitle}>
                <div className={classes.iconWithLabel}>
                  <FaCubes size={16} />
                  <Typography variant="h6">Latest NFTs</Typography>
                </div>
                <Button 
                  className={classes.viewAllButton}
                  onClick={() => navigateTo('/token?new=true&nft=true')}
                  endIcon={<FaArrowRight />}
                >
                  View All
                </Button>
              </div>
              <div className={classes.widgetContent}>
                {loading ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress style={{ color: '#4d79ff' }} />
                  </div>
                ) : (
                  <>
                    {newestNFTs.slice(0, listLimit).map((nft) => (
                      <div
                        key={`newest-${nft.id}`}
                        className={classes.marketplaceItem}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          const addr = nft.contract || nft.id;
                          if (addr) navigateTo(`/links/${addr}`);
                        }}
                      >
                        <a
                          href={nft.contract && nft.tokenId
                            ? `https://etherscan.io/nft/${nft.contract}/${nft.tokenId}`
                            : nft.contract
                              ? `https://etherscan.io/address/${nft.contract}`
                              : undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            // Prevent triggering the /transactions navigation when opening Etherscan
                            e.stopPropagation();
                          }}
                        >
                          <img 
                            src={nft.image} 
                            alt={nft.name} 
                            className={classes.nftImage}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/60x60?text=NFT';
                            }}
                          />
                        </a>
                        <div style={{ flex: 1, textAlign: 'left' }}>
                          <Typography variant="subtitle2">
                            {nft.name === '.nft'
                              ? 'Unknown'
                              : (typeof nft.name === 'string' && nft.name.endsWith('.nft')
                                  ? nft.name.slice(0, -4)
                                  : nft.name)}
                          </Typography>
                          {nft.contract && (
                            <Typography variant="body2" className={classes.textSecondary}>
                              <a
                                href={nft.contract && nft.tokenId
                                  ? `https://etherscan.io/nft/${nft.contract}/${nft.tokenId}`
                                  : `https://etherscan.io/address/${nft.contract}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit', textDecoration: 'none' }}
                                onClick={(e) => {
                                  // Prevent triggering the /transactions navigation when opening Etherscan
                                  e.stopPropagation();
                                }}
                              >
                                {formatAddress(nft.contract)}{nft.tokenId ? ` · ${nft.tokenId}` : ''}
                              </a>
                            </Typography>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </Card>
          </Grid>
        )}
        
        {/* Online Stories Widget */}
        {visibleGroups.includes('stories') && (
          <Grid
            item
            xs={12}
            md={isSingleGroupView && nonOverviewVisible.includes('stories') ? 12 : 6}
          >
            <Card className={classes.widgetCard}>
              <div className={classes.widgetTitle}>
                <div className={classes.iconWithLabel}>
                  <FaUsers size={16} />
                  <Typography variant="h6">Blockchain Stories</Typography>
                </div>
                <Button
                  className={classes.viewAllButton}
                  onClick={() => navigateTo('/stories')}
                  endIcon={<FaArrowRight />}
                >
                  View All Stories
                </Button>
              </div>
              <div className={classes.widgetContent}>
                {loading && (!recentActivity || recentActivity.length === 0) ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress style={{ color: '#4d79ff' }} />
                  </div>
                ) : recentActivity && recentActivity.length > 0 ? (
                  Object.entries(groupStoriesByTime(recentActivity, listLimit)).map(([bucket, stories]) => (
                    <div key={bucket}>
                      <Typography variant="caption" className={classes.storyGroupHeader}>
                        {bucket}
                      </Typography>
                      {stories.map((story) => {
                        const title = story && story.title ? story.title : 'Story';
                        const typeLabel = (story && story.story_type ? String(story.story_type) : 'Story').toUpperCase();
                        const rawSummary =
                          (story && (story.summary || story.description || story.details || story.excerpt)) || '';
                        const summary =
                          typeof rawSummary === 'string' && rawSummary.length
                            ? (rawSummary.length > 160 ? `${rawSummary.slice(0, 157)}...` : rawSummary)
                            : '';
                        const primaryChange =
                          story && typeof story.primary_change_pct === 'number'
                            ? story.primary_change_pct
                            : null;
                        const primaryCount =
                          story && typeof story.primary_count === 'number'
                            ? story.primary_count
                            : null;
                        return (
                          <div key={story.id} className={classes.activityItem}>
                            {(() => {
                              const addr = extractStoryAddress(story);
                              const tokenIcon = getStoryTokenIcon(story);

                              // If we don't yet have an icon from tokenList or cache,
                              // asynchronously fetch it from fetchTokensDB.
                              if (addr && !tokenIcon) {
                                ensureStoryTokenIcon(story);
                              }

                              if (tokenIcon) {
                                return <Avatar className={classes.activityAvatar} src={tokenIcon} />;
                              }
                              if (story && story.icon) {
                                return <Avatar className={classes.activityAvatar} src={story.icon} />;
                              }
                              return (
                                <Avatar className={classes.activityAvatar}>
                                  <FaNewspaper color="#4d79ff" />
                                </Avatar>
                              );
                            })()}
                            <div className={classes.activityContent}>
                              <Typography variant="body2" className={classes.newsfeedTitle}>
                                {title}
                              </Typography>
                              <div className={classes.storyMetaRow}>
                                <Typography variant="caption" className={classes.activityTime}>
                                  {typeLabel}
                                  {' '}
                                  {formatStoryTime(story && story.ts_created)}
                                </Typography>
                                <div>
                                  {primaryChange !== null && (
                                    <span className={classes.storyChip}>
                                      {primaryChange.toFixed(2)}%
                                    </span>
                                  )}
                                  {primaryCount !== null && (
                                    <span className={classes.storyChip}>
                                      {primaryCount}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {summary && (
                                <Typography variant="body2" className={classes.storySummary}>
                                  {summary}
                                </Typography>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                ) : (
                  <Typography variant="body2" className={classes.textSecondary}>
                    No stories available yet.
                  </Typography>
                )}
              </div>
            </Card>
          </Grid>
        )}

        {/* Theses Widget */}
        {visibleGroups.includes('theses') && (
          <Grid
            item
            xs={12}
            md={isSingleGroupView && nonOverviewVisible.includes('theses') ? 12 : 6}
          >
            <Card className={classes.widgetCard}>
              <div className={classes.widgetTitle}>
                <div className={classes.iconWithLabel}>
                  <FaBook size={16} />
                  <Typography variant="h6">Blockchain Theses</Typography>
                </div>
                <Button
                  className={classes.viewAllButton}
                  onClick={() => navigateTo('/thesis')}
                  endIcon={<FaArrowRight />}
                >
                  View All Theses
                </Button>
              </div>
              <div className={classes.widgetContent}>
                {loading && (!recentTheses || recentTheses.length === 0) ? (
                  <div className={classes.loadingContainer}>
                    <CircularProgress style={{ color: '#4d79ff' }} />
                  </div>
                ) : recentTheses && recentTheses.length > 0 ? (
                  Object.entries(groupThesesByTime(recentTheses, listLimit)).map(([bucket, theses]) => (
                    <div key={bucket}>
                      <Typography variant="caption" className={classes.storyGroupHeader}>
                        {bucket}
                      </Typography>
                      {theses.map((t) => {
                        const id = t && t.id != null ? String(t.id) : '';
                        const sym = t && t.entity_symbol ? String(t.entity_symbol) : '';
                        const name = t && t.entity_name ? String(t.entity_name) : '';
                        const addr = t && t.entity_address ? String(t.entity_address) : '';
                        const direction = t && t.direction ? String(t.direction).toUpperCase() : '';
                        const title = sym
                          ? (name ? `${sym} (${name})` : sym)
                          : (name || (addr ? formatAddressLabel(addr) : 'Thesis'));
                        const ts = t && (t.ts_updated || t.ts_created) ? String(t.ts_updated || t.ts_created) : '';
                        const thesisText = t && t.thesis_text ? String(t.thesis_text) : '';
                        const summary = thesisText
                          ? (thesisText.length > 160 ? `${thesisText.slice(0, 157)}...` : thesisText)
                          : '';

                        return (
                          <div
                            key={id || `${title}-${ts}`}
                            className={classes.activityItem}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (id) navigateTo(`/thesis?thesisId=${encodeURIComponent(id)}`);
                              else navigateTo('/thesis');
                            }}
                          >
                            <Avatar className={classes.activityAvatar}>
                              <FaBook color="#4d79ff" />
                            </Avatar>
                            <div className={classes.activityContent}>
                              <Typography variant="body2" className={classes.newsfeedTitle}>
                                {title}
                              </Typography>
                              <div className={classes.storyMetaRow}>
                                <Typography variant="caption" className={classes.activityTime}>
                                  {(direction || 'THESIS')}
                                  {ts ? ` · ${formatStoryTime(ts)}` : ''}
                                </Typography>
                              </div>
                              {summary ? (
                                <Typography variant="body2" className={classes.storySummary}>
                                  {summary}
                                </Typography>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))
                ) : (
                  <Typography variant="body2" className={classes.textSecondary}>
                    No theses available yet.
                  </Typography>
                )}
              </div>
            </Card>
          </Grid>
        )}
        
      </Grid>
      </div>
    </div>
  );
};

export default Dashboard;
